import { createEventHook, createGlobalState } from '@vueuse/core';
import { reactive } from 'vue';
import type { ActivityProgress, Chapter, LearningActivity } from "~/composables/useCourse";
import type { CourseComparison } from "~/types/course.types";
import type { CurrentQuestion, QuestionState, Quiz, QuizAnswer } from "@/types/quiz.types";
import { CourseChapterType, CourseContentActivityType } from "~/enums/course.enums";
import { QuestionAnswersType } from "~/enums/quiz.enums";

const useQuizStore = createGlobalState(() => {
  const { fetchQuiz, resetQuiz, validateQuestionAnswer } = useQuiz();
  const { course } = useCourseState();
  const quizFinished = createEventHook<Quiz>();
  const quizStarted = createEventHook<{ quizId: number; quizType: string | undefined }>();

  const createQuestionState = (questionId: number = 0) => {
    return {
      questionId,
      isCorrect: null,
      isRevealingAnswer: false,
      isValidating: false,
      selectedAnswers: [] as QuizAnswer[],
      correctAnswers: [] as number[],
    } as QuestionState;
  };
  const createQuestionAttempt = () => {
    return {
      isStarted: false,
      isFinished: false,
      isPassed: false,
      score: 0,
    };
  };

  // State
  const state = reactive({
    activeQuizId: null as string | null,
    quiz: null as Quiz | null,
    comparison: null as CourseComparison | null,
    isLoading: false,
    currentQuestion: createQuestionState(0),
    currentAttempt: createQuestionAttempt(),
    remainingQuestions: [] as number[],
  });

  // Getters
  const quiz = computed(() => state.quiz);
  const comparison = computed(() => state.comparison);
  const isLoading = computed(() => state.isLoading);
  const currentAttempt = computed(() => state.currentAttempt);
  const currentQuestion = computed(() => {
    const question = quiz.value?.questions.find(q => q.id === state.currentQuestion.questionId);

    const enrichedQuestion = question ? {
      ...question,
      type: question.multiple ? QuestionAnswersType.Multiple : QuestionAnswersType.Single,
    } : null;

    return {
      ...state.currentQuestion,
      question: enrichedQuestion,
    } as CurrentQuestion;
  });

  const remainingQuestionsCount = computed(() => {
    return state.remainingQuestions.length ?? 0;
  });

  const loadQuiz = async (quizId: string) => {
    reset();
    state.activeQuizId = quizId;

    const fetchedQuiz = await fetchQuiz(quizId);

    if (fetchedQuiz) {
      state.quiz = fetchedQuiz;
      restoreProgress();
    }
  };

  // Actions
  const restoreProgress = () => {
    const progress = state.quiz!.progress;

    state.currentAttempt.isStarted = progress.remainingQuestions.length < state.quiz!.questions.length;

    if (progress.finished) {
      state.currentAttempt.isFinished = true;
      state.currentAttempt.isPassed = progress.passed;
      state.currentAttempt.score = progress.score!;
    }

    state.remainingQuestions = progress.remainingQuestions;
    state.currentQuestion.questionId = progress.remainingQuestions[0];
  };

  const startQuiz = () => {
    state.currentAttempt.isStarted = true;
    if (state.activeQuizId) {
      quizStarted.trigger({
        quizId: parseInt(state.activeQuizId, 10),
        quizType: state.quiz?.type,
      });
    }
  };

  const finishQuiz = () => {
    state.currentAttempt.isFinished = true;

    if (state.quiz) {
      quizFinished.trigger(state.quiz);
    }

    if (!course.value || !course.value.chapters) {
      return false;
    }

    const chapters = [
      ...course.value?.chapters.kickoff || [],
      ...course.value?.chapters.regular || [],
      ...course.value?.chapters.mastery || [],
    ];

    chapters?.some((chapter: Chapter) => {
      if (chapter.contents) {
        chapter.contents.map((content: LearningActivity) => {
          const progress: ActivityProgress | undefined = content.progress;

          if (
            String(content.id) === state?.quiz?.quizId &&
            (!progress || (progress && (!progress.score || state.currentAttempt.score >= progress.score)))
          ) {
            content.progress = {
              finished: state.currentAttempt.isFinished,
              passed: state.currentAttempt.isPassed,
              score: state.currentAttempt.score,
            };

            // unlock regular chapters if kickoff is completed
            if (chapter.type === CourseChapterType.Kickoff && course.value?.chapters?.regular) {
              course.value.chapters.regular = course.value.chapters.regular.map(chapter => {
                chapter.locked = !chapter.contents.some(content => content.hasAccess);

                chapter.contents.forEach(content => {
                  if (content.hasAccess) {
                    content.locked = false;
                  }
                });

                return chapter;
              });
            }

            // unlock mastery chapter if all regular quizzes are passed
            if (chapter.type === CourseChapterType.Regular && course.value?.chapters?.mastery) {
              const allRegularQuizzesPassed = course.value?.chapters?.regular
                ?.every(chapter =>
                  chapter.contents
                    ?.filter(content => content.type === CourseContentActivityType.Quiz)
                    ?.every(content =>
                      content.progress?.passed === true,
                    ),
                );

              if (allRegularQuizzesPassed) {
                course.value.chapters.mastery = course.value.chapters.mastery.map(chapter => {
                  chapter.locked = false;

                  chapter.contents.forEach(content => {
                    if (content.type === CourseContentActivityType.MasteryQuiz) {
                      content.locked = false;
                    }
                  });

                  return chapter;
                });
              }
            }
          }

          return content;
        });
      }
    });
  };

  const restartQuiz = async () => {
    try {
      state.isLoading = true;
      await resetQuiz(state.activeQuizId!);

      // this is to prevent people too quickly re-taking the quiz
      const max = 1700;
      const min = 1300;
      const randomSleep = Math.floor(Math.random() * (max - min + 1) + min);

      await new Promise(resolve => {
        setTimeout(resolve, randomSleep);
      });

      await loadQuiz(state.activeQuizId!);
    } catch(e) {
      console.error(e);
    } finally {
      state.isLoading = false;
    }
  };

  const questionNext = async () => {
    const currentQuestion = state.currentQuestion;
    const isLastQuestion = state.remainingQuestions.length === 0;

    if (isLastQuestion && currentQuestion.isRevealingAnswer) {
      finishQuiz();
      return;
    } else {
      // Set question state to next question
      state.currentQuestion = createQuestionState(state.remainingQuestions[0]);
    }
  };

  const progressAdvance = async (remainingQuestions: number[]) => {
    if (!state.currentAttempt.isStarted) {
      startQuiz();
    } else {
      state.remainingQuestions = remainingQuestions;
      await questionNext();
    }
  };

  const setCurrentQuestionAnswer = (answers: QuizAnswer[]) => {
    const currentQuestion = state.currentQuestion;

    currentQuestion.selectedAnswers = answers;
  };

  const answersValidate = async () => {
    if (!state.activeQuizId) { return; }

    const validation = await validateQuestionAnswer(
      state.activeQuizId,
      state.currentQuestion.questionId,
      state.currentQuestion.selectedAnswers.map(answer => answer.id),
    );

    if (!validation || !validation.value) { return; }

    state.currentQuestion.isRevealingAnswer = true;
    state.currentQuestion.isCorrect = validation.value.isCorrect;
    if (validation.value.correctAnswerIds) {
      state.currentQuestion.correctAnswers.push(...validation.value.correctAnswerIds);
    }

    if (validation.value.progress.finished) {
      currentAttempt.value.isPassed = validation.value.progress.passed;
      currentAttempt.value.score = validation.value.progress.score!;
    }

    return validation.value.progress.remainingQuestions;
  };

  const reset = () => {
    const emptyCurrentQuestion = createQuestionState(0);
    const emptyCurrentAttempt = createQuestionAttempt();
    state.quiz = null;
    state.isLoading = false;
    state.currentQuestion = emptyCurrentQuestion;
    state.currentAttempt = emptyCurrentAttempt;
  };

  return {
    quiz,
    comparison,
    isLoading,
    currentAttempt,
    currentQuestion,
    remainingQuestionsCount,

    loadQuiz,
    startQuiz,
    finishQuiz,
    restartQuiz,
    questionNext,
    answersValidate,
    progressAdvance,
    setCurrentQuestionAnswer,

    onQuizStarted: quizStarted.on,
    onQuizFinished: quizFinished.on,
  };
});

export default useQuizStore;

